import React, { useState, useRef, useEffect } from 'react';
import { translateText } from '../../services/api';

interface RealtimeAudioCaptureProps {
  onTranscriptionUpdate: (text: string) => void;
  onTranslationUpdate: (text: string) => void;
  sourceLanguage: string;
  targetLanguage: string;
  context?: string;
  translationProvider?: string;
  isRecording: boolean;
  onRecordingChange: (isRecording: boolean) => void;
}

const RealtimeAudioCapture: React.FC<RealtimeAudioCaptureProps> = ({
  onTranscriptionUpdate,
  onTranslationUpdate,
  sourceLanguage,
  targetLanguage,
  context,
  translationProvider = 'openrouter',
  isRecording,
  onRecordingChange,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const wsRef = useRef<WebSocket | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const currentContextRef = useRef<string | undefined>(context);
  const isStoppingRef = useRef<boolean>(false);

  useEffect(() => {
    // Update context ref when prop changes
    currentContextRef.current = context;
  }, [context]);

  useEffect(() => {
    // Listen for context update events
    const handleContextUpdate = (event: CustomEvent) => {
      const newContext = event.detail;
      currentContextRef.current = newContext;
      
      // Send context update to WebSocket if connected
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        const configMessage: {
          type: string;
          language: string;
          target_language: string;
          context?: string;
        } = {
          type: 'config',
          language: sourceLanguage,
          target_language: targetLanguage,
        };
        
        if (newContext && newContext.trim()) {
          configMessage.context = newContext.trim();
        }
        
        wsRef.current.send(JSON.stringify(configMessage));
        console.log('Context updated:', newContext);
      }
    };

    window.addEventListener('updateContext', handleContextUpdate as EventListener);
    
    return () => {
      // Cleanup on unmount
      window.removeEventListener('updateContext', handleContextUpdate as EventListener);
      stopRecording();
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [sourceLanguage, targetLanguage]);

  const startRecording = async () => {
    try {
      setError(null);
      setIsConnecting(true);

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      streamRef.current = stream;

      // Initialize AudioContext for PCM conversion
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextClass({ sampleRate: 16000 });
      audioContextRef.current = audioContext;

      // Create WebSocket connection
      const wsUrl =
        process.env.REACT_APP_WS_URL ||
        `ws://localhost:8000/ws/realtime-transcribe`;
      
      console.log('Connecting to WebSocket:', wsUrl);
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('WebSocket connected');
        
        // Send initial configuration
        const configMessage: {
          type: string;
          language: string;
          target_language: string;
          context?: string;
        } = {
          type: 'config',
          language: sourceLanguage,
          target_language: targetLanguage,
        };
        
        // Add context if provided
        if (context && context.trim()) {
          configMessage.context = context.trim();
        }
        
        ws.send(JSON.stringify(configMessage));

        // Wait a moment for config to be processed, then start recording
        setTimeout(() => {
          setIsConnecting(false);
          onRecordingChange(true);

          // Create audio source and processor for PCM conversion
          const source = audioContext.createMediaStreamSource(stream);
          sourceRef.current = source;

          // Use ScriptProcessorNode to convert to PCM16
          const bufferSize = 4096;
          const processor = audioContext.createScriptProcessor(bufferSize, 1, 1);
          processorRef.current = processor;

          processor.onaudioprocess = (e) => {
            if (ws.readyState === WebSocket.OPEN) {
              const inputData = e.inputBuffer.getChannelData(0);
              // Convert Float32Array to Int16Array (PCM16)
              const pcm16 = new Int16Array(inputData.length);
              for (let i = 0; i < inputData.length; i++) {
                // Clamp and convert to 16-bit integer
                const s = Math.max(-1, Math.min(1, inputData[i]));
                pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
              }

              // Convert to base64
              const buffer = pcm16.buffer;
              const uint8Array = new Uint8Array(buffer);
              const base64 = btoa(Array.from(uint8Array, byte => String.fromCharCode(byte)).join(''));

              // Send PCM audio chunk
              ws.send(
                JSON.stringify({
                  type: 'audio',
                  data: base64,
                  format: 'pcm16',
                })
              );
            }
          };

          source.connect(processor);
          processor.connect(audioContext.destination);
        }, 500);
      };

      ws.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'transcription') {
            onTranscriptionUpdate(data.text);

            // Automatically translate
            if (data.text && data.text.trim()) {
              try {
                // Use current context from ref (may have been updated)
                const currentContext = currentContextRef.current;
                const translation = await translateText(
                  data.text,
                  sourceLanguage,
                  targetLanguage,
                  null, // model - use default
                  translationProvider, // provider
                  currentContext || null // context
                );
                onTranslationUpdate(translation.translated_text);
              } catch (err) {
                console.error('Translation error:', err);
              }
            }
          } else if (data.type === 'error') {
            setError(data.message || 'An error occurred');
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        console.error('WebSocket URL:', wsUrl);
        setError('Lỗi kết nối. Vui lòng kiểm tra backend có đang chạy không và thử lại.');
        setIsConnecting(false);
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        // Don't show error if we're intentionally stopping or it's a normal closure
        if (!isStoppingRef.current && event.code !== 1000 && event.code !== 1005) {
          setError(`Kết nối bị đóng (code: ${event.code}). Vui lòng kiểm tra backend và thử lại.`);
        }
        setIsConnecting(false);
        onRecordingChange(false);
        isStoppingRef.current = false;
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('Error starting recording:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to access microphone. Please check permissions.'
      );
      setIsConnecting(false);
    }
  };

  const stopRecording = () => {
    // Mark that we're intentionally stopping
    isStoppingRef.current = true;
    
    // Disconnect audio processor
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (wsRef.current) {
      if (wsRef.current.readyState === WebSocket.OPEN) {
        // Send stop message before closing
        try {
          wsRef.current.send(JSON.stringify({ type: 'stop' }));
          // Wait a bit for backend to process stop message before closing
          setTimeout(() => {
            if (wsRef.current) {
              wsRef.current.close(1000, 'Recording stopped');
              wsRef.current = null;
            }
          }, 100);
        } catch (err) {
          console.error('Error sending stop message:', err);
          // Close immediately if send fails
          wsRef.current.close();
          wsRef.current = null;
        }
      } else {
        // WebSocket is not open, just close it
        wsRef.current.close();
        wsRef.current = null;
      }
    }

    onRecordingChange(false);
  };

  const handleToggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={handleToggleRecording}
          disabled={isConnecting}
          className={`px-4 py-2 rounded-lg font-semibold text-white transition-all ${
            isRecording
              ? 'bg-red-500 hover:bg-red-600'
              : 'bg-green-500 hover:bg-green-600'
          } disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2`}
        >
          {isConnecting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Connecting...</span>
            </>
          ) : isRecording ? (
            <>
              <div className="w-3 h-3 bg-white rounded-full"></div>
              <span>Stop</span>
            </>
          ) : (
            <>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
                />
              </svg>
              <span>Start</span>
            </>
          )}
        </button>

        {isRecording && (
          <div className="flex items-center gap-2 text-red-500">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium">Recording</span>
          </div>
        )}
      </div>

      {error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded-md text-red-700 text-xs">
          {error}
        </div>
      )}

      {!isRecording && !error && (
        <p className="text-xs text-gray-500">
          Click to start. Allow microphone access.
        </p>
      )}
    </div>
  );
};

export default RealtimeAudioCapture;

